/**
 * EnquiryDetail — right-hand pane on /enquiries.
 *
 * Shows the full record, status pipeline, quick actions (call/email/SMS
 * prefill), activity timeline, audit log, and Resolve/Reopen buttons.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  X, Phone, Mail, MessageSquare, Loader2, CheckCircle2, RotateCcw, Archive, Clock,
  User, PhoneCall, Send, ClipboardCheck, History, Users as UsersIcon, Sparkles, Copy, Check, Pencil, GitMerge,
} from "lucide-react";
import EnquiryQuickForm from "@/components/enquiries/EnquiryQuickForm";
import { EnquiryMergeDialog } from "@/components/enquiries/EnquiryMerge";
import { CustomerStatusPicker } from "@/components/enquiries/CustomerStatus";
import { AuditEntry } from "@/components/enquiries/AuditEntry";
import { NextActionCard } from "@/components/enquiries/NextAction";


const STATUS_OPTIONS = [
  { key: "new",              label: "New" },
  { key: "assigned",         label: "Assigned" },
  { key: "in_progress",      label: "In progress" },
  { key: "waiting_client",   label: "Waiting on client" },
  { key: "waiting_internal", label: "Waiting internally" },
  { key: "scheduled",        label: "Scheduled" },
  { key: "closed_no_action", label: "Closed — no action" },
];

export const statusLabel = (s) => (s === "resolved" ? "Resolved"
  : STATUS_OPTIONS.find((o) => o.key === s)?.label || String(s || "").replace(/_/g, " "));

const ACTIVITY_TYPES = [
  { key: "attempted_call", label: "Attempted call",  icon: PhoneCall },
  { key: "connected_call", label: "Connected call",  icon: Phone },
  { key: "voicemail",      label: "Left voicemail",  icon: MessageSquare },
  { key: "sms",            label: "SMS sent",        icon: MessageSquare },
  { key: "email",          label: "Email sent",      icon: Mail },
  { key: "face_to_face",   label: "Face-to-face",    icon: User },
  { key: "internal",       label: "Internal action", icon: ClipboardCheck },
  { key: "other",          label: "Other",           icon: Clock },
];

const RESOLUTION_OPTIONS = [
  "booked", "info_sent", "waitlisted", "referral_required",
  "declined", "unsuitable", "no_response", "other",
];


export default function EnquiryDetail({ enquiryId, categories, categoryGroups, onChanged, onClose, onSelect }) {
  const { user } = useAuth();
  const isManager = user && ["owner", "manager"].includes(user.role);
  const [enq, setEnq] = useState(null);
  const [activities, setActivities] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActivity, setShowActivity] = useState(false);
  const [showResolve, setShowResolve] = useState(false);
  const [showReopen, setShowReopen] = useState(false);
  const [showDraftReply, setShowDraftReply] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [tab, setTab] = useState("activity");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rEnq, rAct, rAudit] = await Promise.all([
        api.get(`/enquiries/${enquiryId}`),
        api.get(`/enquiries/${enquiryId}/activities`),
        api.get(`/enquiries/${enquiryId}/audit`),
      ]);
      setEnq(rEnq.data);
      setActivities(rAct.data || []);
      setAudit(rAudit.data || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [enquiryId]);

  useEffect(() => { load(); }, [load]);

  async function changeStatus(status) {
    try {
      await api.post(`/enquiries/${enquiryId}/status`, { status });
      toast.success(`Status → ${statusLabel(status)}`);
      load(); onChanged?.();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function archive() {
    if (!window.confirm("Archive (delete) this enquiry? It's removed from the list and from the KPI lead counts.")) return;
    try {
      await api.delete(`/enquiries/${enquiryId}`);
      toast.success("Archived");
      onChanged?.(); onClose?.();
    } catch (e) {
      toast.error("Archive failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  if (loading || !enq) {
    return (
      <Card className="p-6 rounded-md shadow-none border border-zinc-200 text-sm text-zinc-500 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </Card>
    );
  }

  const contact = enq.contact || {};
  const isResolved = enq.status === "resolved";

  return (
    <Card className="p-4 rounded-md shadow-none border border-zinc-200 space-y-4" data-testid={`enquiry-detail-${enquiryId}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-zinc-950 truncate">{enq.subject}</h2>
          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="uppercase tracking-wider text-2xs px-1.5 py-0.5 rounded border bg-zinc-100 border-zinc-200" data-testid="enquiry-detail-status">
              {statusLabel(enq.status)}
            </span>
            <span className="uppercase tracking-wider text-2xs">{enq.priority}</span>
            {enq.category_name && <span>· {enq.category_name}</span>}
            {enq.created_at && <span data-testid="enquiry-detail-received">· received {new Date(enq.created_at).toLocaleString()}</span>}
            {enq.due_at && <span>· due {new Date(enq.due_at).toLocaleString()}</span>}
          </div>
        </div>
        <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="enquiry-detail-close">
          <X className="h-4 w-4" />
        </button>
      </div>

      <NextActionCard enq={enq} onChanged={() => { load(); onChanged?.(); }} />

      {/* Contact strip with click-to actions */}
      <div className="border border-zinc-200 rounded-md p-2 flex items-center gap-3 flex-wrap bg-zinc-50 text-sm">
        <User className="h-4 w-4 text-zinc-500" />
        {contact.unknown ? (
          <span className="italic text-zinc-500">Unknown caller</span>
        ) : (
          <>
            <span className="font-medium text-zinc-900">{contact.name || "(no name)"}</span>
            {contact.mobile && (
              <a href={`tel:${contact.mobile}`} className="text-blue-700 hover:underline text-xs inline-flex items-center gap-0.5" data-testid="enquiry-tel">
                <Phone className="h-3 w-3" /> {contact.mobile}
              </a>
            )}
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="text-blue-700 hover:underline text-xs inline-flex items-center gap-0.5" data-testid="enquiry-mailto">
                <Mail className="h-3 w-3" /> {contact.email}
              </a>
            )}
            {contact.mobile && (
              <a href={`sms:${contact.mobile}`} className="text-blue-700 hover:underline text-xs inline-flex items-center gap-0.5" data-testid="enquiry-sms">
                <MessageSquare className="h-3 w-3" /> SMS
              </a>
            )}
          </>
        )}
      </div>

      {!contact.unknown && (
        <div className="flex items-center gap-2 flex-wrap -mt-2">
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">Customer status</Label>
          <CustomerStatusPicker enq={enq} onChanged={() => { load(); onChanged?.(); }} />
        </div>
      )}

      {/* Message body */}
      {enq.message && (
        <div className="text-sm text-zinc-800 whitespace-pre-wrap border-l-2 border-zinc-200 pl-3">
          {enq.message}
        </div>
      )}

      {/* Meta Lead Ads — marketing attribution + form answers (spec §10). */}
      {enq.source_channel === "meta_lead_ad" && (
        <div
          className="rounded-md border border-blue-200 bg-blue-50/40 p-3 space-y-2 text-xs"
          data-testid="enquiry-meta-lead-panel"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="uppercase tracking-wider text-2xs font-semibold text-blue-900 bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded">
              {enq.platform === "instagram" ? "Instagram Lead Ad"
                : enq.platform === "facebook" ? "Facebook Lead Ad"
                : "Meta Lead Ad"}
            </span>
            {enq.integration && (
              <span className="text-2xs uppercase tracking-wider text-blue-700">
                via {enq.integration}
              </span>
            )}
            {enq.external_id && (
              <code className="text-2xs font-mono text-blue-800 bg-white border border-blue-200 rounded px-1 py-0.5">
                {enq.external_id}
              </code>
            )}
          </div>
          {enq.marketing_attribution && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
              {enq.marketing_attribution.campaign_name && (
                <div><span className="text-blue-700 uppercase tracking-wider text-2xs">Campaign</span>{" "}
                  <span className="text-blue-900">{enq.marketing_attribution.campaign_name}</span></div>
              )}
              {enq.marketing_attribution.adset_name && (
                <div><span className="text-blue-700 uppercase tracking-wider text-2xs">Ad set</span>{" "}
                  <span className="text-blue-900">{enq.marketing_attribution.adset_name}</span></div>
              )}
              {enq.marketing_attribution.ad_name && (
                <div><span className="text-blue-700 uppercase tracking-wider text-2xs">Ad</span>{" "}
                  <span className="text-blue-900">{enq.marketing_attribution.ad_name}</span></div>
              )}
              {enq.marketing_attribution.form_name && (
                <div><span className="text-blue-700 uppercase tracking-wider text-2xs">Form</span>{" "}
                  <span className="text-blue-900">{enq.marketing_attribution.form_name}</span></div>
              )}
            </div>
          )}
          {enq.form_answers && Object.keys(enq.form_answers).length > 0 && (
            <div>
              <div className="text-blue-700 uppercase tracking-wider text-2xs mt-1 mb-0.5">
                Instant form responses
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5">
                {Object.entries(enq.form_answers).map(([q, a]) => (
                  <div key={q} className="contents">
                    <dt className="text-blue-800 font-medium">{q}:</dt>
                    <dd className="text-blue-900">
                      {Array.isArray(a) ? a.join(", ") : String(a)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      )}

      {/* Status pipeline */}
      {!isResolved && (
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">Move to</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => changeStatus(s.key)}
                disabled={s.key === enq.status}
                className={`text-xs px-2 py-1 rounded-md border ${s.key === enq.status ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}
                data-testid={`enquiry-status-${s.key}`}
              >{s.label}</button>
            ))}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => setShowActivity(true)}
          className="bg-zinc-950 text-white text-xs"
          data-testid="enquiry-log-activity-btn"
        >
          <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Log contact attempt
        </Button>
        <Button
          onClick={() => setShowEdit(true)}
          variant="outline"
          className="text-xs"
          data-testid="enquiry-edit-btn"
        >
          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
        </Button>
        <Button
          onClick={() => setShowDraftReply(true)}
          variant="outline"
          className="text-xs text-indigo-800 border-indigo-300 hover:bg-indigo-50"
          data-testid="enquiry-draft-reply-btn"
        >
          <Sparkles className="h-3.5 w-3.5 mr-1" /> Draft reply
        </Button>
        {!isResolved && (
          <Button
            onClick={() => setShowResolve(true)}
            variant="outline"
            className="text-xs text-emerald-800 border-emerald-300 hover:bg-emerald-50"
            data-testid="enquiry-resolve-btn"
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Resolve
          </Button>
        )}
        {isResolved && (
          <Button
            onClick={() => setShowReopen(true)}
            variant="outline"
            className="text-xs"
            data-testid="enquiry-reopen-btn"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen
          </Button>
        )}
        {isManager && (
          <Button
            onClick={() => setShowMerge(true)}
            variant="outline"
            className="text-xs"
            data-testid="enquiry-merge-btn"
          >
            <GitMerge className="h-3.5 w-3.5 mr-1" /> Merge duplicate
          </Button>
        )}
        {isManager && (
          <Button
            onClick={archive}
            variant="outline"
            className="text-xs text-red-700 hover:bg-red-50"
            data-testid="enquiry-archive-btn"
          >
            <Archive className="h-3.5 w-3.5 mr-1" /> Archive
          </Button>
        )}
      </div>

      {/* Resolved outcome (read-only) */}
      {isResolved && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 text-sm" data-testid="enquiry-resolution">
          <div className="text-2xs uppercase tracking-wider text-emerald-800 mb-1 flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" /> Resolved
          </div>
          <div><b>{enq.resolution_category}</b></div>
          <div className="text-zinc-800 whitespace-pre-wrap mt-1">{enq.outcome_notes}</div>
          {enq.commercial?.outcome && (
            <div className="text-2xs text-emerald-900 mt-2">
              Commercial: <b>{enq.commercial.outcome}</b>
              {enq.commercial.estimated_value != null && <> · ${Number(enq.commercial.estimated_value).toFixed(2)}</>}
            </div>
          )}
        </div>
      )}

      {/* Tabs: activities | audit */}
      <div className="border-b border-zinc-200 flex gap-1">
        {[
          { key: "activity", label: `Activity (${activities.length})`, icon: History },
          { key: "audit",    label: `Audit (${audit.length})`,          icon: UsersIcon },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border-b-2 -mb-px ${tab === t.key ? "border-zinc-950 text-zinc-950 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
            data-testid={`enquiry-tab-${t.key}`}
          >
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "activity" && (
        <div data-testid="enquiry-activity-list">
          {activities.length === 0 ? (
            <div className="text-sm text-zinc-500 italic">No contact attempts logged yet.</div>
          ) : (
            <ul className="space-y-2">
              {activities.map((a) => {
                const meta = ACTIVITY_TYPES.find((t) => t.key === a.activity_type) || ACTIVITY_TYPES[0];
                const Icon = meta.icon;
                return (
                  <li key={a.id} className="border border-zinc-200 rounded-md p-2 text-sm" data-testid={`enquiry-activity-${a.id}`}>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <Icon className="h-3.5 w-3.5" />
                      <b className="text-zinc-800">{meta.label}</b>
                      <span>· {a.actor_name}</span>
                      <span className="ml-auto">{new Date(a.created_at).toLocaleString()}</span>
                    </div>
                    {a.note && <div className="text-sm text-zinc-800 mt-1 whitespace-pre-wrap">{a.note}</div>}
                    {(a.result || a.next_action) && (
                      <div className="text-2xs text-zinc-600 mt-1">
                        {a.result && <>Result: {a.result}. </>}
                        {a.next_action && <>Next: {a.next_action}{a.next_action_at ? ` on ${new Date(a.next_action_at).toLocaleString()}` : ""}.</>}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {tab === "audit" && (
        <div data-testid="enquiry-audit-list">
          {audit.length === 0 ? (
            <div className="text-sm text-zinc-500 italic">No audit entries.</div>
          ) : (
            <ul className="space-y-1.5">
              {audit.map((e) => <AuditEntry key={e.id} entry={e} />)}
            </ul>
          )}
        </div>
      )}

      {showActivity && (
        <ActivityDialog
          enquiryId={enquiryId}
          onClose={() => setShowActivity(false)}
          onSaved={() => { setShowActivity(false); load(); onChanged?.(); }}
        />
      )}
      {showResolve && (
        <ResolveDialog
          enquiryId={enquiryId}
          onClose={() => setShowResolve(false)}
          onSaved={() => { setShowResolve(false); load(); onChanged?.(); }}
        />
      )}
      {showReopen && (
        <ReopenDialog
          enquiryId={enquiryId}
          onClose={() => setShowReopen(false)}
          onSaved={() => { setShowReopen(false); load(); onChanged?.(); }}
        />
      )}
      {showDraftReply && (
        <DraftReplyDialog
          enq={enq}
          onClose={() => setShowDraftReply(false)}
        />
      )}
      {showEdit && (
        <EnquiryQuickForm
          enquiry={enq}
          categories={categories || []}
          categoryGroups={categoryGroups || {}}
          onClose={() => setShowEdit(false)}
          onCreated={() => { setShowEdit(false); load(); onChanged?.(); }}
        />
      )}
      {showMerge && (
        <EnquiryMergeDialog
          enq={enq}
          onClose={() => setShowMerge(false)}
          onMerged={(keepId) => { setShowMerge(false); onChanged?.(); if (keepId === enq.id) load(); else onSelect?.(keepId); }}
        />
      )}
    </Card>
  );
}


function DraftReplyDialog({ enq, onClose }) {
  const [channel, setChannel] = useState(
    (enq?.contact?.email) ? "email" : "sms",
  );
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);   // { channel, subject, body, signature, model }
  const [edited, setEdited] = useState("");
  const [editedSubject, setEditedSubject] = useState("");
  const [templates, setTemplates] = useState([]);
  const [signatureBlock, setSignatureBlock] = useState(""); // resolved from /me signature
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Load templates + the user's current signature block on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [tRes, sRes] = await Promise.all([
          api.get("/reply-templates"),
          api.get("/settings/my-signature"),
        ]);
        if (!alive) return;
        setTemplates(tRes.data || []);
        const s = sRes.data?.signature || {};
        // Build a plain-text block that matches what the backend
        // appends (so preview + one-click template insert produce the
        // same signed message).
        const fname = (s.name || "").split(" ")[0];
        const clinic = s.clinic || "";
        setSignatureBlock(JSON.stringify({ full: s, sms: `— ${fname}${clinic ? `, ${clinic}` : ""}` }));
      } catch { /* ignore — signature/templates are optional */ }
    })();
    return () => { alive = false; };
  }, []);

  async function generate() {
    setLoading(true);
    setDraft(null);
    try {
      const r = await api.post(`/enquiries/${enq.id || enq._id}/draft-reply`, { channel });
      setDraft(r.data);
      setEdited(r.data.body || "");
      setEditedSubject(r.data.subject || "");
    } catch (e) {
      toast.error("Draft failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }

  function _fillPlaceholders(text) {
    const name = (enq?.contact?.name || "").trim();
    const firstName = name.split(" ")[0] || "there";
    return (text || "")
      .replace(/\{\{\s*first_name\s*\}\}/gi, firstName)
      .replace(/\{\{\s*name\s*\}\}/gi, name || firstName);
  }

  function applyTemplate(tmpl) {
    if (!tmpl) return;
    // Switch to the template's channel if different, otherwise reuse
    // the current selection.
    if (tmpl.channel !== channel) setChannel(tmpl.channel);
    let body = _fillPlaceholders(tmpl.body || "");
    // Append the user's signature so template-inserted drafts also
    // carry the footer. Try to reuse the last-generated signature —
    // otherwise skip (user can hit Re-draft for a full signed version).
    try {
      const sig = JSON.parse(signatureBlock || "{}");
      if (tmpl.channel === "sms" && sig.sms) {
        body = body.replace(/\s+$/, "") + `\n${sig.sms}`;
      }
    } catch { /* ignore */ }
    setEdited(body);
    setEditedSubject(tmpl.channel === "email" ? _fillPlaceholders(tmpl.subject || "") : "");
    setDraft({ channel: tmpl.channel, body, subject: tmpl.subject, signature: null, model: `template:${tmpl.name}` });
    toast.success(`Applied "${tmpl.name}"`);
  }

  async function saveAsTemplate() {
    const defaultName = editedSubject || edited.slice(0, 40).replace(/\n/g, " ");
    const name = window.prompt("Template name?", defaultName);
    if (!name?.trim()) return;
    setSavingTemplate(true);
    try {
      const r = await api.post("/reply-templates", {
        name: name.trim(),
        channel,
        subject: channel === "email" ? editedSubject : "",
        body: edited,
        is_shared: true,
      });
      setTemplates((t) => [...t, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Saved as template — will appear in the picker next time");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSavingTemplate(false);
    }
  }

  function copyDraft() {
    const text = channel === "email" && editedSubject
      ? `Subject: ${editedSubject}\n\n${edited}`
      : edited;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  }

  function openMailto() {
    const to = enq?.contact?.email || "";
    const params = new URLSearchParams();
    if (editedSubject) params.set("subject", editedSubject);
    if (edited) params.set("body", edited);
    window.open(`mailto:${to}?${params.toString()}`);
  }

  function openSms() {
    const to = enq?.contact?.mobile || "";
    window.open(`sms:${to}?body=${encodeURIComponent(edited)}`);
  }

  const bodyWithoutSignature = draft?.signature && edited.endsWith(draft.signature)
    ? edited.slice(0, edited.length - draft.signature.length).replace(/[\s\n]+$/, "")
    : edited;
  const signatureVisible = draft?.signature && edited.endsWith(draft.signature);

  // Rank templates so ones tagged with this enquiry's service float to
  // the front of the chip strip. Falls back to alphabetical.
  const serviceTag = (enq?.service_of_interest || "")
    .trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const channelTemplates = templates
    .filter((t) => t.channel === channel)
    .map((t) => {
      const tags = (t.tags || []).map((x) => String(x).toLowerCase());
      const matchesService = !!serviceTag && (
        tags.includes(serviceTag) ||
        (serviceTag.includes("physio") && tags.some((x) => x.includes("physio"))) ||
        (serviceTag.includes("exercise") && tags.some((x) => x.includes("exercise") || x.includes("ep"))) ||
        (serviceTag.includes("pilates") && tags.some((x) => x.includes("pilates")))
      );
      return { ...t, _matchesService: matchesService };
    })
    .sort((a, b) => {
      if (a._matchesService !== b._matchesService) return b._matchesService - a._matchesService;
      return (a.name || "").localeCompare(b.name || "");
    });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="draft-reply-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600" /> Draft AI reply
          </DialogTitle>
          <DialogDescription>
            The model will read this enquiry and write a warm, ready-to-send response.
            Your signature is auto-appended. Nothing is sent — you review and copy or open in your SMS / email app.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setChannel("sms")}
              disabled={!enq?.contact?.mobile}
              className={`text-xs px-3 py-1.5 rounded-md border ${channel === "sms" ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300"} disabled:opacity-40`}
              data-testid="draft-reply-channel-sms"
            >
              <MessageSquare className="h-3.5 w-3.5 mr-1 inline" /> SMS
            </button>
            <button
              onClick={() => setChannel("email")}
              disabled={!enq?.contact?.email}
              className={`text-xs px-3 py-1.5 rounded-md border ${channel === "email" ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300"} disabled:opacity-40`}
              data-testid="draft-reply-channel-email"
            >
              <Mail className="h-3.5 w-3.5 mr-1 inline" /> Email
            </button>
            <Button
              onClick={generate}
              disabled={loading}
              size="sm"
              className="ml-auto bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
              data-testid="draft-reply-generate"
            >
              {loading
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Drafting…</>
                : draft ? <><Sparkles className="h-3.5 w-3.5 mr-1" /> Re-draft</>
                        : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Generate</>}
            </Button>
          </div>

          {channelTemplates.length > 0 && (
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2" data-testid="draft-reply-templates">
              <div className="text-2xs uppercase tracking-wider text-zinc-500 font-medium mb-1.5 px-1 flex items-center justify-between">
                <span>One-click templates</span>
                {serviceTag && channelTemplates.some((t) => t._matchesService) && (
                  <span className="normal-case tracking-normal text-emerald-700">
                    ★ tagged for <b>{enq.service_of_interest}</b> shown first
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {channelTemplates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t)}
                    className={`text-2xs px-2 py-1 rounded-full border transition-colors ${
                      t._matchesService
                        ? "bg-emerald-50 border-emerald-300 text-emerald-900 hover:bg-emerald-100"
                        : "bg-white border-zinc-300 text-zinc-800 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-900"
                    }`}
                    data-testid={`draft-reply-template-${t.id}`}
                    title={t.body?.slice(0, 120) + (t.body?.length > 120 ? "…" : "")}
                  >
                    {t._matchesService && <span className="mr-0.5">★</span>}
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {draft && (
            <>
              {channel === "email" && (
                <div>
                  <Label className="text-2xs uppercase tracking-wider text-zinc-500">Subject</Label>
                  <Input
                    value={editedSubject}
                    onChange={(e) => setEditedSubject(e.target.value)}
                    className="mt-1"
                    data-testid="draft-reply-subject"
                  />
                </div>
              )}
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">
                  {channel === "sms" ? `SMS (${edited.length}/320)` : "Body"}
                </Label>
                <Textarea
                  value={edited}
                  onChange={(e) => setEdited(e.target.value)}
                  rows={channel === "sms" ? 5 : 12}
                  className="mt-1 font-mono-num text-sm"
                  data-testid="draft-reply-body"
                />
                {signatureVisible && (
                  <div
                    className="mt-1.5 text-2xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 flex items-start gap-1"
                    data-testid="draft-reply-signature-tag"
                  >
                    <Check className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <span>
                      Signature auto-appended from your profile.
                      Edit the last {draft.signature.split("\n").filter(Boolean).length} line(s) above if it needs tweaking.
                    </span>
                  </div>
                )}
                {draft?.signature && !signatureVisible && (
                  <button
                    type="button"
                    className="mt-1.5 text-2xs text-zinc-500 hover:text-zinc-950 underline"
                    onClick={() => {
                      setEdited((e) => e.replace(/\s+$/, "") + (channel === "sms" ? "\n" : "\n\n") + draft.signature);
                    }}
                    data-testid="draft-reply-signature-restore"
                  >
                    Restore signature
                  </button>
                )}
              </div>
              <p className="text-2xs text-zinc-500">Drafted by {draft.model}. Edit before sending.</p>
            </>
          )}

          {!draft && !loading && (
            <div className="p-4 text-center text-xs text-zinc-500 border border-dashed rounded-md">
              Pick a channel and hit Generate.
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          {draft && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={saveAsTemplate}
                disabled={savingTemplate || !edited?.trim()}
                data-testid="draft-reply-save-template"
                title="Save this reply as a reusable template"
              >
                {savingTemplate
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving…</>
                  : <>💾 Save as template</>}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={copyDraft}
                data-testid="draft-reply-copy"
              >
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy
              </Button>
              {channel === "sms" && enq?.contact?.mobile && (
                <Button
                  size="sm"
                  onClick={openSms}
                  className="bg-zinc-950 text-white"
                  data-testid="draft-reply-open-sms"
                >
                  <Send className="h-3.5 w-3.5 mr-1" /> Open SMS
                </Button>
              )}
              {channel === "email" && enq?.contact?.email && (
                <Button
                  size="sm"
                  onClick={openMailto}
                  className="bg-zinc-950 text-white"
                  data-testid="draft-reply-open-email"
                >
                  <Send className="h-3.5 w-3.5 mr-1" /> Open email
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ActivityDialog({ enquiryId, onClose, onSaved }) {
  const [type, setType] = useState("attempted_call");
  const [note, setNote] = useState("");
  const [result, setResult] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [nextAt, setNextAt] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.post(`/enquiries/${enquiryId}/activities`, {
        activity_type: type,
        note,
        result: result || null,
        next_action: nextAction || null,
        next_action_at: nextAt || null,
      });
      toast.success("Activity logged");
      onSaved?.();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="enquiry-activity-dialog">
        <DialogHeader>
          <DialogTitle>Log a contact attempt</DialogTitle>
          <DialogDescription className="text-xs">
            Records who did what and when. Contributes to response-time reporting.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-2xs">Type</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {ACTIVITY_TYPES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setType(t.key)}
                  className={`text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1 ${type === t.key ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300"}`}
                  data-testid={`activity-type-${t.key}`}
                >
                  <t.icon className="h-3 w-3" /> {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-2xs">Note</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="activity-note" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-2xs">Result (optional)</Label>
              <Input value={result} onChange={(e) => setResult(e.target.value)} placeholder="No answer, voicemail…" data-testid="activity-result" />
            </div>
            <div>
              <Label className="text-2xs">Next action</Label>
              <Input value={nextAction} onChange={(e) => setNextAction(e.target.value)} placeholder="Retry tomorrow" data-testid="activity-next-action" />
            </div>
          </div>
          <div>
            <Label className="text-2xs">Next action at</Label>
            <Input type="datetime-local" value={nextAt} onChange={(e) => setNextAt(e.target.value)} data-testid="activity-next-at" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-zinc-950 text-white" data-testid="activity-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Log activity"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ResolveDialog({ enquiryId, onClose, onSaved }) {
  const [category, setCategory] = useState("booked");
  const [notes, setNotes] = useState("");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!notes.trim()) return toast.error("Outcome notes are required to resolve");
    setSaving(true);
    try {
      await api.post(`/enquiries/${enquiryId}/resolve`, {
        resolution_category: category,
        outcome_notes: notes,
        commercial: {
          outcome: category,
          estimated_value: value ? parseFloat(value) : null,
        },
      });
      toast.success("Resolved");
      onSaved?.();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="enquiry-resolve-dialog">
        <DialogHeader>
          <DialogTitle>Resolve enquiry</DialogTitle>
          <DialogDescription className="text-xs">
            Record the outcome. This closes any linked task and stops
            reminders. Reopen later if needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-2xs">Resolution</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 h-9 w-full px-2 border border-zinc-300 rounded-md text-sm bg-white"
              data-testid="resolve-category"
            >
              {RESOLUTION_OPTIONS.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-2xs">Outcome notes (required)</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="resolve-notes" />
          </div>
          <div>
            <Label className="text-2xs">Estimated value (optional)</Label>
            <Input type="number" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} data-testid="resolve-value" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-700 text-white" data-testid="resolve-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Resolve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ReopenDialog({ enquiryId, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!reason.trim()) return toast.error("A reason is required");
    setSaving(true);
    try {
      await api.post(`/enquiries/${enquiryId}/reopen`, { reason });
      toast.success("Reopened");
      onSaved?.();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="enquiry-reopen-dialog">
        <DialogHeader>
          <DialogTitle>Reopen enquiry</DialogTitle>
          <DialogDescription className="text-xs">
            Original resolution is preserved in the audit log.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-2xs">Reason</Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reopen-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-zinc-950 text-white" data-testid="reopen-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Reopen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
